/**
 * Search SKU Detector
 * Automatically redirects to correct tab (kids/adult) based on SKU pattern
 * 
 * SKU Patterns:
 * - ABE = Adult (Abercrombie & Fitch)
 * - ABK = Kids (Abercrombie Kids)
 */

class SearchSKUDetector {
  constructor() {
    this.init();
  }

  init() {
    // Kids is hidden for PH – disable SKU-based kids/adult switching
    return;

    // Only run on search page
    if (!window.location.pathname.includes('/search')) {
      return;
    }

    // Get search query from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');
    
    if (!searchQuery) {
      return;
    }

    // Detect SKU pattern and redirect if needed
    this.detectAndRedirect(searchQuery, urlParams);
  }

  /**
   * Detect SKU pattern in search query
   * @param {string} query - Search query
   * @returns {string|null} - 'adult', 'kids', or null
   */
  detectSKUPattern(query) {
    // Convert to uppercase for consistent matching
    const upperQuery = query.toUpperCase();
    
    // Check for ABK (Kids) pattern - must be consecutive
    if (upperQuery.includes('ABK')) {
      console.log('🎯 Kids SKU detected (ABK pattern)');
      return 'kids';
    }
    
    // Check for ABE (Adult) pattern - must be consecutive
    if (upperQuery.includes('ABE')) {
      console.log('🎯 Adult SKU detected (ABE pattern)');
      return 'adult';
    }
    
    return null;
  }

  /**
   * Redirect to correct tab if SKU pattern detected
   * @param {string} searchQuery - Search query string
   * @param {URLSearchParams} urlParams - Current URL parameters
   */
  detectAndRedirect(searchQuery, urlParams) {
    const detectedType = this.detectSKUPattern(searchQuery);
    const currentView = urlParams.get('view');
    const currentVendor = urlParams.get('vendor');
    
    // No SKU pattern detected, no action needed
    if (!detectedType) {
      console.log('ℹ️ No SKU pattern detected in search query');
      return;
    }

    // Determine if redirect is needed
    let needsRedirect = false;
    let targetView = null;

    if (detectedType === 'kids') {
      // Need redirect if not on kids view OR vendor is not ABERCROMBIE KIDS
      if (currentView !== 'kids' || currentVendor !== 'ABERCROMBIE KIDS') {
        needsRedirect = true;
        targetView = 'kids';
        console.log('🔄 Redirecting to Kids tab (ABK SKU detected)');
      }
    } else if (detectedType === 'adult') {
      // Need redirect if on kids view OR vendor is not ABERCROMBIE & FITCH
      if (currentView === 'kids' || currentVendor !== 'ABERCROMBIE & FITCH') {
        needsRedirect = true;
        targetView = null; // Remove view parameter for adult
        console.log('🔄 Redirecting to Adult tab (ABE SKU detected)');
      }
    }

    // Perform redirect if needed
    if (needsRedirect) {
      this.performRedirect(urlParams, targetView);
    } else {
      console.log('✅ Already on correct tab with correct vendor for SKU type');
    }
  }

  /**
   * Perform the redirect with correct view parameter
   * @param {URLSearchParams} urlParams - Current URL parameters
   * @param {string|null} targetView - Target view ('kids' or null for adult)
   */
  performRedirect(urlParams, targetView) {
    // Clone current params
    const newParams = new URLSearchParams(urlParams);
    
    // Update view and vendor parameters based on target
    if (targetView === 'kids') {
      newParams.set('view', 'kids');
      newParams.set('vendor', 'ABERCROMBIE KIDS');
      console.log('🎯 Setting vendor to: ABERCROMBIE KIDS');
    } else {
      newParams.delete('view'); // Remove view for adult
      newParams.set('vendor', 'ABERCROMBIE & FITCH');
      console.log('🎯 Setting vendor to: ABERCROMBIE & FITCH');
    }
    
    // Build new URL
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    
    console.log('🚀 Redirecting to:', newUrl);
    window.location.href = newUrl;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SearchSKUDetector());
} else {
  new SearchSKUDetector();
}
